import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig({}));
  }, []);
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig() {
  return useContext(ConfigContext) || {};
}

// ---- Placeholder detection: hide unset/placeholder contact details on the public site ----
export function isPlaceholderPhone(v) {
  if (!v) return true;
  let d = (v || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);   // strip +91 country code
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1); // strip trunk 0
  if (d.length < 10) return true;                 // too short to be a real number
  if (/^0+$/.test(d)) return true;                // all zeros
  if (d.includes("00000")) return true;           // seed placeholder pattern
  if (/(\d)\1{4,}/.test(d)) return true;          // 5+ repeated digits e.g. 99999 / 11111
  if (/^(0123456789|1234567890)$/.test(d)) return true; // obvious sequential dummies
  if (d.length === 10 && !/^[6-9]/.test(d)) return true; // valid Indian mobiles start 6-9
  return false;
}
export function isPlaceholderEmail(v) {
  return !v || !v.includes("@") || /(?:\.example|example\.(?:com|org|net|in))$/i.test(v.trim());
}
export function isPlaceholderAddress(v) {
  return !v || /placeholder/i.test(v) || v.trim().length < 8;
}
export function isPlaceholderMaps(v) {
  if (!v || !/^https?:\/\//i.test(v.trim())) return true;
  return /^https?:\/\/(?:www\.)?(?:maps\.google\.com|google\.com\/maps)\/?$/i.test(v.trim());
}
// Returns the value if it's real, otherwise null (so callers can hide it).
export function realPhone(v) { return isPlaceholderPhone(v) ? null : v; }
export function realEmail(v) { return isPlaceholderEmail(v) ? null : v; }
export function realAddress(v) { return isPlaceholderAddress(v) ? null : v; }
export function realMaps(v) { return isPlaceholderMaps(v) ? null : v; }

// Normalize a WhatsApp number to E.164 digits (no +), applying an India default CC once.
// Returns null for placeholder/invalid numbers so callers can hide the action entirely.
export function normalizeWhatsApp(number, defaultCc = "91") {
  if (!number || isPlaceholderPhone(number)) return null;
  const hadPlus = String(number).trim().startsWith("+");
  let d = String(number).replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);              // strip 00 international prefix
  while (d.length > 12 && d.startsWith("9191")) d = d.slice(2); // collapse duplicated +91 country codes
  if (d.length === 12 && d.startsWith("91")) {          // already has +91 country code
    return /^[6-9]\d{9}$/.test(d.slice(2)) ? d : null;  // local part must be a valid Indian mobile
  }
  if (d.length === 11 && d.startsWith("0")) d = d.slice(1); // strip trunk 0
  if (d.length === 10 && /^[6-9]/.test(d)) return defaultCc + d; // bare Indian mobile -> add CC once
  if (hadPlus && d.length >= 8 && d.length <= 15) return d;      // explicit international number
  return null;
}

export function waLink(number, text) {
  const digits = normalizeWhatsApp(number);
  if (!digits) return null;
  const msg = encodeURIComponent(text || "Hello Maha Sampada, I have a Maharashtra travel requirement.");
  return `https://wa.me/${digits}?text=${msg}`;
}

// Human-readable, normalized display (e.g. "+91 95195 12013") — never shows a duplicated country code.
export function waDisplay(number) {
  const d = normalizeWhatsApp(number);
  if (!d) return "";
  if (d.length === 12 && d.startsWith("91")) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
  return `+${d}`;
}

// Admin settings-completion checklist
export const SETTINGS_CHECKLIST = [
  ["phone", "Main phone", (c) => !isPlaceholderPhone(c.phone)],
  ["whatsapp", "WhatsApp", (c) => !isPlaceholderPhone(c.whatsapp)],
  ["email_general", "General email", (c) => !isPlaceholderEmail(c.email_general)],
  ["email_partners", "Partner email", (c) => !isPlaceholderEmail(c.email_partners)],
  ["email_queries", "Queries email", (c) => !isPlaceholderEmail(c.email_queries)],
  ["email_contracting", "Contracting email", (c) => !isPlaceholderEmail(c.email_contracting)],
  ["address", "Address", (c) => !isPlaceholderAddress(c.address)],
  ["business_hours", "Business hours", (c) => !!(c.business_hours && c.business_hours.trim())],
  ["maps_link", "Google Maps link", (c) => !isPlaceholderMaps(c.maps_link)],
  ["logo_url", "Desktop logo", (c) => !!c.logo_url],
  ["mobile_logo_url", "Mobile logo", (c) => !!c.mobile_logo_url],
  ["favicon_url", "Favicon", (c) => !!c.favicon_url],
  ["og_image_url", "Social-sharing image", (c) => !!c.social_image_url],
];

export function settingsCompletion(config) {
  const c = config || {};
  const items = SETTINGS_CHECKLIST.map(([key, label, ok]) => ({ key, label, ok: ok(c) }));
  const done = items.filter((i) => i.ok).length;
  const essentials = ["phone", "whatsapp", "email_general", "address"];
  const essentialsComplete = items.filter((i) => essentials.includes(i.key)).every((i) => i.ok);
  return { items, done, total: items.length, essentialsComplete };
}
